import { useEffect } from 'react'

import { Button, Form, SidebarModal } from '@domain-group/suit-theme'

import { capitalizeString } from '@domain-group/javascript-utils'

import { getNotifiableTypeFromAudience } from '@functions/notification'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useNotification, { useNotificationForm } from '@hooks/useNotification'

import EntitySelector from '@components/EntitySelector'

import LocationSelector from './LocationSelector'

interface ModalCallbacks {
  closeModal: () => void,
  createNotification: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateNotificationModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'CreateNotificationModal', showModal } = props
  const { closeModal, createNotification } = callbacks

  const notificationPayload = useNotification()
  const notificationFormPayload = useNotificationForm({}, { validateOn: 'change' })

  const {
    callbacks: {
      createNotification: createFn,
    },
    creating,
  } = notificationPayload

  const {
    entityState,
    entityState: {
      audience,
      body,
      end_date,
      notifiable_id,
      options,
      start_date,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = notificationFormPayload

  const {
    dismissable,
    link,
    location,
    primary_link_title,
    primary_link_url,
    secondary_link_title,
    secondary_link_url,
  } = options || {}

  const createNotificationPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Notification Created',
  }

  useEffect(() => {
    const notifiableType = audience !== 'global' ? getNotifiableTypeFromAudience(audience) : ''
    setEntityState({ notifiable_id: undefined, notifiable_type: notifiableType })
  }, [audience])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Notification" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Audience" errorMessage={errors.audience}>
            {/*
             // @ts-expect-error TS2322 */}
            <select name="audience" value={audience} {...handlers}>
              <option value="">Please Select...</option>
              <option value="global">Global</option>
              <option value="agencies">Agencies</option>
              <option value="brands">Brands</option>
              <option value="providers">Providers</option>
            </select>
          </Form.Field>

          {!!audience && audience !== 'global' && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2322
                selectItem: (entity: { id?: number }) => setEntityState({ notifiable_id: entity ? entity.id : null }),
              }}
              entityKey={audience}
              errorMessage={errors.notifiable_id}
              marginTop="large"
              searchForwardProps={{ placeholder: `All ${capitalizeString(audience)}` }}
              selectedItemId={notifiable_id}
              title={`* ${getNotifiableTypeFromAudience(audience)}`}
            />
          )}

          <LocationSelector notificationFormPayload={notificationFormPayload} />

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Message" errorMessage={errors.body} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="body" style={{ minHeight: 100 }} value={body} {...handlers} />
          </Form.Field>

          {!location && (
            <Form.Field label="Link" marginTop="large">
              <input name="link" value={link} {...handlers} />
            </Form.Field>
          )}

          {!!location && (
            <>
              <Form.Field label="Dismissable" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select
                  data-value-type="boolean"
                  name="options.dismissable"
                  value={dismissable}
                  {...handlers}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </Form.Field>

              <Form.Row marginTop="large" columns="repeat(2, 1fr)">
                <Form.Field label="Start Date" errorMessage={errors.start_date} hideErrorMessage>
                  <input type="date" name="start_date" value={start_date} {...handlers} />
                </Form.Field>

                <Form.Field label="End Date" errorMessage={errors.end_date} hideErrorMessage>
                  <input type="date" name="end_date" value={end_date} {...handlers} />
                </Form.Field>
              </Form.Row>

              <Form.Field
                label="Primary Link Title"
                errorMessage={errors['options.primary_link_title']}
                hideErrorMessage
                marginTop="large"
              >
                <input name="options.primary_link_title" value={primary_link_title} {...handlers} />
              </Form.Field>

              <Form.Field
                label="Primary Link"
                errorMessage={errors['options.primary_link_url']}
                hideErrorMessage
                marginTop="large"
              >
                <input name="options.primary_link_url" value={primary_link_url} {...handlers} />
              </Form.Field>

              <Form.Field
                label="Secondary Link Title"
                errorMessage={errors['options.secondary_link_title']}
                hideErrorMessage
                marginTop="large"
              >
                <input name="options.secondary_link_title" value={secondary_link_title} {...handlers} />
              </Form.Field>

              <Form.Field
                label="Secondary Link"
                errorMessage={errors['options.secondary_link_url']}
                hideErrorMessage
                marginTop="large"
              >
                <input name="options.secondary_link_url" value={secondary_link_url} {...handlers} />
              </Form.Field>
            </>
          )}

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createNotification(createNotificationPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Notification' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateNotificationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
